import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { normalize } from '../utils/text'
import { derived, mapAccess } from '../utils/worldQueries'
import { monsterKind } from '../utils/monsterKind'
import { imageEntryForMonster } from '../utils/entityImages'
import type { PresenceKind } from '../utils/worldQueries'
import type { Monster, Spawn, WorldData, WorldMap } from '../types/world'

export type ExplorerTab = 'mapas' | 'monstruos' | 'npc' | 'apariciones'
export type KindFilter = 'todos' | PresenceKind

const toNum = (s: string) => {
  if (s.trim() === '') return undefined
  const n = Number(s)
  return Number.isFinite(n) ? n : undefined
}

/** Estado de filtros guardado en la URL para que la vista se pueda compartir. */
export function useMapsExplorer(w: WorldData) {
  const [params, setParams] = useSearchParams()
  const tab = (params.get('vista') as ExplorerTab) || 'mapas'
  const q = params.get('q') ?? ''
  const mapFilter = params.get('mapa') ?? ''
  const kind = (params.get('tipo') as KindFilter) || 'todos'
  const lvMin = params.get('nmin') ?? ''
  const lvMax = params.get('nmax') ?? ''
  const charLv = params.get('pj') ?? ''

  const set = (key: string, value: string) =>
    setParams(
      (prev) => {
        const n = new URLSearchParams(prev)
        if (value) n.set(key, value)
        else n.delete(key)
        return n
      },
      { replace: true },
    )

  const clear = () => setParams((prev) => (prev.get('vista') ? new URLSearchParams({ vista: prev.get('vista')! }) : new URLSearchParams()), { replace: true })

  const results = useMemo(() => {
    const d = derived(w)
    const nq = normalize(q.trim())
    const min = toNum(lvMin)
    const max = toNum(lvMax)
    const pj = toNum(charLv)
    const mapId = mapFilter === '' ? undefined : Number(mapFilter)

    const lvOk = (monsterId: number) => {
      if (min === undefined && max === undefined) return true
      const l = w.monsters.get(monsterId)?.level
      if (l === undefined) return false
      return (min === undefined || l >= min) && (max === undefined || l <= max)
    }
    const accessOk = (id: number) => mapAccess(w.maps.get(id), pj) === 'yes'
    const kindOk = (set?: Set<PresenceKind>) => kind === 'todos' || !!set?.has(kind)
    const nameHas = (name?: string) => !!name && normalize(name).includes(nq)

    const maps: WorldMap[] = []
    let undocumentedAccess = 0
    w.maps.forEach((m) => {
      if (mapId !== undefined && m.id !== mapId) return
      const mons = Array.from(d.mapMonsters.get(m.id) ?? [])
      if (nq && !nameHas(m.name) && !mons.some((id) => nameHas(w.monsters.get(id)?.name))) return
      if (!kindOk(d.mapKinds.get(m.id))) return
      if ((min !== undefined || max !== undefined) && !mons.some(lvOk)) return
      if (pj !== undefined) {
        const a = mapAccess(m, pj)
        if (a === 'unknown') undocumentedAccess++
        if (a !== 'yes') return
      }
      maps.push(m)
    })

    const monsters: Monster[] = []
    const npcList: Monster[] = []
    w.monsters.forEach((m) => {
      const where = Array.from(d.monsterMaps.get(m.id) ?? [])
      if (nq && !nameHas(m.name)) return
      if (mapId !== undefined && !where.includes(mapId)) return
      if (!kindOk(d.monsterKinds.get(m.id))) return
      if (!lvOk(m.id)) return
      if (pj !== undefined && !where.some(accessOk)) return
      if (monsterKind(w, m) === 'npc') npcList.push(m)
      else monsters.push(m)
    })
    // Primero los que tienen foto, luego los que no; dentro de cada grupo, por nivel (NPC: por nombre).
    const noPhoto = (id: number) => (imageEntryForMonster(w, id)?.image ? 0 : 1)
    monsters.sort((a, b) => noPhoto(a.id) - noPhoto(b.id) || (a.level ?? 9999) - (b.level ?? 9999) || a.name.localeCompare(b.name))
    npcList.sort((a, b) => noPhoto(a.id) - noPhoto(b.id) || a.name.localeCompare(b.name))

    const spawns: Spawn[] =
      kind === 'evento'
        ? []
        : w.spawns.filter((s) => {
            if (mapId !== undefined && s.mapId !== mapId) return false
            if (kind !== 'todos' && (s.kind === 'invasion' ? 'invasion' : 'spot') !== kind) return false
            if (nq && !nameHas(w.monsters.get(s.monsterId)?.name) && !nameHas(w.maps.get(s.mapId)?.name)) return false
            if (!lvOk(s.monsterId)) return false
            if (pj !== undefined && !accessOk(s.mapId)) return false
            return true
          })

    return { maps, monsters, npcs: npcList, spawns, undocumentedAccess, pj }
  }, [w, q, mapFilter, kind, lvMin, lvMax, charLv])

  const activeFilters = [mapFilter, kind !== 'todos' ? kind : '', lvMin, lvMax, charLv].filter(Boolean).length

  return { tab, q, mapFilter, kind, lvMin, lvMax, charLv, set, clear, results, activeFilters }
}
